import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import { Dialog } from '../../../../Dialog/Dialog';
import { Button } from '../../../../Button/Button';
import OcInput from '../../../../Input/OcInput';
import Typography from '../../../../Typography/Typography';
import Checkbox from '../../../../Checkbox';
import { SaveQuickReport } from '../utils/DataTableQuickReports.types';
import { UdsMessages, useIntlUds } from '../../../../../translations/UdsIntlProvider';

interface Props {
  saveQuickReport: SaveQuickReport | undefined;
  setSaveQuickReport: (saveQuickReport: SaveQuickReport) => void;
  onSave: () => void;
}

export const AddQuickReportDialog: FC<React.PropsWithChildren<Props>> = ({
  saveQuickReport,
  setSaveQuickReport,
  onSave
}) => {
  const { intl } = useIntlUds();
  const shouldShowPinSwitch = saveQuickReport?.actionType === 'Add' || saveQuickReport?.actionType === 'Save as';

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (shouldShowPinSwitch) {
      setSaveQuickReport({
        ...saveQuickReport,
        name: saveQuickReport?.name,
        // Pin Quick Report by default
        pinned: true
      });
    }
  }, [shouldShowPinSwitch]);

  return (
    <Dialog
      dataTest="quick-report-dialog"
      title={getTitle(saveQuickReport?.actionType, intl)}
      open={typeof saveQuickReport?.name === 'string' && saveQuickReport?.actionType !== 'Delete'}
      onClose={() => setSaveQuickReport(undefined)}
      showCancelButton
      actions={[
        <Button
          key="save-quick-report"
          dataTest="quick-report-submit-button"
          variant="contained"
          color="primary"
          onClick={onSave}
          disabled={!!saveQuickReport?.name && saveQuickReport?.name?.length < 1}
        >
          {intl.DataTable.QuickViews.AddQuickReportDialog.saveBtn}
        </Button>
      ]}
    >
      <OcInput
        label={intl.DataTable.QuickViews.AddQuickReportDialog.nameInput.label}
        autoFocus
        fullWidth
        dataTest="quick-report-name-input"
        value={saveQuickReport?.name ?? ''}
        onChange={(e) =>
          setSaveQuickReport({
            ...saveQuickReport,
            name: e.target.value,
            quickReportId: saveQuickReport?.quickReportId,
            actionType: saveQuickReport?.actionType
          })
        }
      />
      {shouldShowPinSwitch && (
        <StyledTypography>
          <Checkbox
            dataTest="quick-report-pinned-checkbox"
            checked={!!saveQuickReport?.pinned}
            onChange={() =>
              setSaveQuickReport({
                ...saveQuickReport,
                name: saveQuickReport?.name,
                pinned: !saveQuickReport?.pinned
              })
            }
          >
            {intl.DataTable.QuickViews.AddQuickReportDialog.pinQuickViewCheckbox.label}
          </Checkbox>
        </StyledTypography>
      )}
    </Dialog>
  );
};

const getTitle = (
  actionType: 'Rename' | 'Save as' | 'Save' | 'Duplicate' | 'Delete' | 'Add' | undefined,
  intl: UdsMessages
) => {
  if (actionType === 'Add') return intl.DataTable?.QuickViews.AddQuickReportDialog.title.Add;
  return actionType ? intl.DataTable?.QuickViews.AddQuickReportDialog.title[actionType] : '';
};

const StyledTypography = styled(Typography)`
  padding-top: ${({ theme }) => theme.spacingSize.l};
`;
